/**
 * NC (National Contract) template formulas.
 * Moved from bidAnalysisFormulas.ts; shared formulas are in shared/formulas.
 */

import { getShortCodeFromFullName } from '../../utils/gsmsHandlingTypeUtils';

/**
 * Calculate WAC for NC (National Contract)
 * Formula: Suggested Bid Price + 0.15
 */
export const calculateWACForNC = (suggestedBidPrice: number): number => {
  return suggestedBidPrice + 0.15;
};

/**
 * Calculate GSMS Net Cost From Mfg (NC) - same logic as shared but documented for NC.
 */
export const calculateNCGSMSNetCostFromMfg = (
  gsmsCostFromMfg: number,
  supplierBulkNdc?: string,
  supplierDiscount?: number
): number => {
  if (gsmsCostFromMfg === 0) return gsmsCostFromMfg;
  if (supplierDiscount !== undefined && supplierDiscount !== null) {
    return gsmsCostFromMfg * (1 - supplierDiscount);
  }
  if (supplierBulkNdc && supplierBulkNdc.length >= 5) {
    return gsmsCostFromMfg;
  }
  return gsmsCostFromMfg;
};

export const calculateNCGSMSHandlingTypeAbbreviation = (gsmsHandlingType: string): string => {
  return getShortCodeFromFullName(gsmsHandlingType) || ' ';
};

export const calculateNCGSMSBottleCapacity = (
  gsmsHandlingType: string,
  gsmsPackSize: number
): string => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  if (!shortCode || shortCode === ' ') return '';
  if (gsmsPackSize > 1 && gsmsPackSize < 181) return '120cc';
  if (gsmsPackSize > 180 && gsmsPackSize < 501) return '500cc';
  if (gsmsPackSize > 500) return '950cc';
  return '';
};

export const calculateNCGSMSBottleCost = (
  gsmsHandlingType: string,
  gsmsBottleCapacity: string
): number => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  if (shortCode !== 'R') return 0;
  switch (gsmsBottleCapacity) {
    case '120cc': return 0.084;
    case '500cc': return 0.238;
    case '950cc': return 0.572;
    default: return 0;
  }
};

export const calculateNCGSMSCapCost = (
  gsmsBottleCost: number,
  gsmsBottleCapacity: string
): number => {
  if (gsmsBottleCost === 0) return 0;
  switch (gsmsBottleCapacity) {
    case '120cc': return 0.042;
    case '500cc': return 0.073;
    case '950cc': return 0.096;
    default: return 0;
  }
};

export const calculateNCGSMSLabelCost = (
  gsmsHandlingType: string,
  gsmsBottleCost: number
): number => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  if (shortCode === 'OL') return 0.15;
  if (gsmsBottleCost === 0) return 0;
  return 0.03;
};

export const calculateNCGSMSInsertCost = (
  gsmsBottleCost: number,
  supplierName: string
): number => {
  if (gsmsBottleCost === 0) return 0;
  if (supplierName === 'Apotex') return 0.15;
  return 0.07;
};

export const calculateNCGSMSIffFee = (suggestedBidPrice: number): number => {
  return suggestedBidPrice * 0.005;
};

export const calculateNCGSMSBoxCartonCost = (
  gsmsBottleCost: number,
  gsmsBottleCapacity: string
): number => {
  if (gsmsBottleCost === 0) return 0;
  switch (gsmsBottleCapacity) {
    case '120cc': return 0.009;
    case '500cc': return 0.039;
    case '950cc': return 0.127;
    default: return 0;
  }
};

export const calculateNCOutsourceRepackFee = (gsmsHandlingType: string): number => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  return shortCode === 'PL2' ? 0.33 : 0;
};

export const calculateNCGSMSFrtCost = (
  gsmsBottleCapacity: string,
  outsourceRepackFee: number
): number => {
  if (gsmsBottleCapacity === '' || gsmsBottleCapacity === ' ') return 0;
  if (outsourceRepackFee === 0) {
    return gsmsBottleCapacity === '120cc' ? 0.1 : 0.25;
  }
  return 0;
};

export const calculateNCPV2Percent = (
  gsmsBottleCapacity: string,
  wac: number
): number => {
  if (!gsmsBottleCapacity || gsmsBottleCapacity.trim() === '' || gsmsBottleCapacity === ' ') return 0;
  if (!wac || isNaN(wac) || wac <= 0) return 0;
  return wac * 0.02;
};

export const calculateNCGSMSLabor = (
  bottleCapacity: string | null | undefined,
  bottleCost: number | string | null | undefined,
  outsourceRepackFee: number | string | null | undefined = 0
): number => {
  if (!bottleCapacity || bottleCapacity === '' || bottleCapacity === null || bottleCapacity === undefined) {
    return 0;
  }
  const bottleCostNum = typeof bottleCost === 'string' ? parseFloat(bottleCost) : (bottleCost || 0);
  const outsourceRepackFeeNum = typeof outsourceRepackFee === 'string' ? parseFloat(outsourceRepackFee) : (outsourceRepackFee || 0);
  if (bottleCostNum !== 0 && bottleCostNum !== null && !isNaN(bottleCostNum)) {
    return 0.07;
  }
  if (bottleCostNum === 0 && outsourceRepackFeeNum === 0) {
    return 0.02;
  }
  if (bottleCostNum === 0 && outsourceRepackFeeNum !== 0) {
    return 0;
  }
  return 0;
};

export const calculateNCGSMSCost = (
  gsmsNetCostFromMfg: number,
  mfgBottleSize: number,
  gsmsPackSize: number,
  gsmsBottleCost: number,
  gsmsCapCost: number,
  gsmsLabelCost: number,
  gsmsInsertCost: number,
  gsmsIffFee: number,
  gsmsBoxCartonCost: number,
  gsmsDessicantCost: number,
  outsourceRepackFee: number,
  blendedWhlsFee: number,
  gsmsFrtCost: number,
  pv2Percent: number,
  gsmsLabor: number
): number => {
  if (mfgBottleSize === 0) return 0;
  const ratio = gsmsNetCostFromMfg / mfgBottleSize;
  const component1 = ratio * gsmsPackSize;
  const component2 = gsmsBottleCost + gsmsCapCost + gsmsLabelCost + gsmsInsertCost +
    gsmsIffFee + gsmsBoxCartonCost + (gsmsDessicantCost || 0) + outsourceRepackFee +
    blendedWhlsFee + gsmsFrtCost + pv2Percent + gsmsLabor;
  return component1 + component2;
};

/** Returns ratio 0..1 (not percentage). */
export const calculateNCGSMSProfit = (
  suggestedBidPrice: number,
  gsmsCost: number
): number => {
  if (!suggestedBidPrice || suggestedBidPrice === 0) return 0;
  return (suggestedBidPrice - gsmsCost) / suggestedBidPrice;
};

export const calculateNCEstAnnualUsage = (
  vaAnnualNcEst: number,
  dodAnnualNcEst: number,
  ihsAnnualNcEst: number,
  bopAnnualNcEst: number
): number => {
  return vaAnnualNcEst + dodAnnualNcEst + ihsAnnualNcEst + bopAnnualNcEst;
};

export const calculateNCContractAnnualRevenue = (
  ncEstAnnualUsage: number,
  suggestedBidPrice: number
): number => {
  return ncEstAnnualUsage * suggestedBidPrice;
};

export const calculateNCContractAnnualGP = (
  contractAnnualRevenue: number,
  gsmsProfit: number
): number => {
  return contractAnnualRevenue * gsmsProfit;
};

export const calculateNCContractAnnualCost = (
  gsmsCost: number,
  ncEstAnnualUsage: number
): number => {
  return gsmsCost * ncEstAnnualUsage;
};

export const calculateNCGovernmentSavingsAnalysis = (
  lowestContractPrice: number,
  suggestedBidPrice: number,
  ncEstAnnualUsage: number
): number => {
  return (lowestContractPrice - suggestedBidPrice) * ncEstAnnualUsage;
};

export const calculateNCGrossProfit = (
  totalContractAnnualGP: number,
  totalContractAnnualRevenue: number
): number => {
  if (!totalContractAnnualRevenue || totalContractAnnualRevenue === 0) return 0;
  return (totalContractAnnualGP / totalContractAnnualRevenue) * 100;
};

export const calculateNCGPPerUnit = (
  totalContractAnnualGP: number,
  ncEstAnnualUsage: number
): number => {
  if (!ncEstAnnualUsage || ncEstAnnualUsage === 0) return 0;
  return totalContractAnnualGP / ncEstAnnualUsage;
};
